/**
 * localPPlansApi — RTK Query slice cho /purchase/local-p-plans/*.
 * Tag type: 'LocalPPlan'.
 */
import { baseApi } from './baseApi'
import type {
  DataResponse,
  LocalPPlanCreateIn,
  LocalPPlanListItem,
  LocalPPlanListQuery,
  LocalPPlanOut,
  LocalPPlanUpdateIn,
  MessageResponse,
  PageResponse,
} from '@/types/api'

export const localPPlansApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listPlans: b.query<PageResponse<LocalPPlanListItem>, LocalPPlanListQuery | void>({
      query: (params) => ({
        url: '/purchase/local-p-plans/list',
        params: params ?? {},
      }),
      providesTags: (res, _e, arg) => {
        const contractNo = (arg && (arg as LocalPPlanListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items
                .filter((p): p is LocalPPlanListItem & { id: number } => p.id != null)
                .map((p) => ({ type: 'LocalPPlan' as const, id: p.id })),
              { type: 'LocalPPlan' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'LocalPPlan' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'LocalPPlan' as const, id: 'LIST' }]
      },
    }),
    createPlan: b.mutation<DataResponse<LocalPPlanOut>, LocalPPlanCreateIn>({
      query: (body) => ({
        url: '/purchase/local-p-plans/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'LocalPPlan', id: 'LIST' }],
    }),
    updatePlan: b.mutation<
      DataResponse<LocalPPlanOut>,
      { planId: number; body: LocalPPlanUpdateIn }
    >({
      query: ({ planId, body }) => ({
        url: `/purchase/local-p-plans/${planId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'LocalPPlan', id: arg.planId },
        { type: 'LocalPPlan', id: 'LIST' },
      ],
    }),
    deletePlan: b.mutation<MessageResponse, number>({
      query: (planId) => ({
        url: `/purchase/local-p-plans/${planId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, planId) => [
        { type: 'LocalPPlan', id: planId },
        { type: 'LocalPPlan', id: 'LIST' },
      ],
    }),
    deleteAllPlans: b.mutation<MessageResponse, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p-plans/by-contract/${encodeURIComponent(contractNo)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'LocalPPlan', id: 'LIST' }],
    }),
  }),
})

export const {
  useListPlansQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanMutation,
  useDeleteAllPlansMutation,
} = localPPlansApi
